import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth, API } from '../context/AuthContext';
import axios from 'axios';
import {
  Megaphone,
  Users,
  Buildings,
  FileText,
  TrendUp,
  ArrowUp,
  ArrowRight,
  CalendarBlank
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

export function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (['super_admin', 'org_admin'].includes(user?.role)) {
          const { data } = await axios.get(`${API}/analytics/global`, { withCredentials: true });
          setStats(data);
        } else {
          // For other roles, fetch basic stats
          const [campaigns, submissions] = await Promise.all([
            axios.get(`${API}/campaigns`, { withCredentials: true }),
            axios.get(`${API}/submissions/count`, { withCredentials: true })
          ]);
          setStats({
            total_campaigns: campaigns.data.length,
            active_campaigns: campaigns.data.filter(c => c.status === 'published').length,
            total_submissions: submissions.data.count,
            submissions_today: 0,
            submissions_this_week: 0,
            top_campaigns: campaigns.data.slice(0, 5).map(c => ({
              campaign_id: c.id,
              campaign_name: c.name,
              submission_count: c.submission_count
            }))
          });
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  const statCards = [
    {
      label: 'Kampagnen gesamt',
      value: stats?.total_campaigns || 0,
      icon: Megaphone,
      color: 'bg-[#002FA7]',
      link: '/campaigns'
    },
    {
      label: 'Aktive Kampagnen',
      value: stats?.active_campaigns || 0,
      icon: TrendUp,
      color: 'bg-emerald-500',
      link: '/campaigns?status=published'
    },
    {
      label: 'Einreichungen gesamt',
      value: stats?.total_submissions || 0,
      icon: FileText,
      color: 'bg-amber-500',
      link: '/submissions'
    },
    {
      label: 'Heutige Einreichungen',
      value: stats?.submissions_today || 0,
      icon: CalendarBlank,
      color: 'bg-purple-500',
      link: '/submissions'
    }
  ];

  if (['super_admin', 'org_admin'].includes(user?.role)) {
    statCards.push(
      {
        label: 'Abteilungen',
        value: stats?.total_departments || 0,
        icon: Buildings,
        color: 'bg-teal-500',
        link: '/departments'
      },
      {
        label: 'Benutzer gesamt',
        value: stats?.total_users || 0,
        icon: Users,
        color: 'bg-pink-500',
        link: '/users'
      }
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-black tracking-tight text-zinc-900">
            Übersicht
          </h1>
          <p className="text-zinc-500 mt-1">
            Willkommen zurück, {user?.name}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
          {statCards.slice(0, 4).map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.label}
                to={stat.link}
                className={`card-dashboard p-6 animate-fade-in stagger-${index + 1}`}
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                      {stat.label}
                    </p>
                    <p className="text-3xl font-black text-zinc-900 mt-2">
                      {loading ? '-' : stat.value.toLocaleString()}
                    </p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-sm`}>
                    <Icon className="w-5 h-5 text-white" weight="bold" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Additional Stats for Admins */}
        {['super_admin', 'org_admin'].includes(user?.role) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {statCards.slice(4).map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Link
                  key={stat.label}
                  to={stat.link}
                  className={`card-dashboard p-6 animate-fade-in stagger-${index + 5}`}
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                        {stat.label}
                      </p>
                      <p className="text-3xl font-black text-zinc-900 mt-2">
                        {loading ? '-' : stat.value.toLocaleString()}
                      </p>
                    </div>
                    <div className={`${stat.color} p-3 rounded-sm`}>
                      <Icon className="w-5 h-5 text-white" weight="bold" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Campaigns */}
          <div className="card-dashboard p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-900">Top-Kampagnen</h2>
              <Link to="/campaigns" className="text-sm text-[#002FA7] hover:underline flex items-center gap-1">
                Alle ansehen <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-12 bg-zinc-100 animate-pulse rounded-sm" />
                ))}
              </div>
            ) : stats?.top_campaigns?.length > 0 ? (
              <div className="space-y-2">
                {stats.top_campaigns.map((campaign, index) => (
                  <div
                    key={campaign.campaign_id}
                    className="flex items-center justify-between p-3 bg-zinc-50 rounded-sm hover:bg-zinc-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 flex items-center justify-center bg-[#002FA7] text-white text-xs font-bold rounded-sm">
                        {index + 1}
                      </span>
                      <span className="font-medium text-zinc-900 truncate max-w-[200px]">
                        {campaign.campaign_name}
                      </span>
                    </div>
                    <span className="text-sm text-zinc-500">
                      {campaign.submission_count} Einreichungen
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-8">Noch keine Kampagnen vorhanden</p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card-dashboard p-6">
            <h2 className="text-lg font-bold text-zinc-900 mb-4">Schnellaktionen</h2>
            <div className="space-y-3">
              <Link
                to="/campaigns/new"
                className="flex items-center gap-3 p-4 border border-zinc-200 rounded-sm hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-all group"
                data-testid="quick-action-new-campaign"
              >
                <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center group-hover:bg-[#002FA7] transition-colors">
                  <Megaphone className="w-5 h-5 text-[#002FA7] group-hover:text-white" />
                </div>
                <div>
                  <p className="font-medium text-zinc-900">Kampagne erstellen</p>
                  <p className="text-sm text-zinc-500">Neues Event oder neue Aktion anlegen</p>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-400 ml-auto group-hover:text-[#002FA7]" />
              </Link>

              <Link
                to="/templates"
                className="flex items-center gap-3 p-4 border border-zinc-200 rounded-sm hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-all group"
                data-testid="quick-action-templates"
              >
                <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                  <FileText className="w-5 h-5 text-amber-600 group-hover:text-white" />
                </div>
                <div>
                  <p className="font-medium text-zinc-900">Templates verwalten</p>
                  <p className="text-sm text-zinc-500">Ticket-Layouts gestalten</p>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-400 ml-auto group-hover:text-[#002FA7]" />
              </Link>

              <Link
                to="/submissions"
                className="flex items-center gap-3 p-4 border border-zinc-200 rounded-sm hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-all group"
                data-testid="quick-action-submissions"
              >
                <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
                  <TrendUp className="w-5 h-5 text-emerald-600 group-hover:text-white" />
                </div>
                <div>
                  <p className="font-medium text-zinc-900">Einreichungen ansehen</p>
                  <p className="text-sm text-zinc-500">Erfasste Daten prüfen</p>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-400 ml-auto group-hover:text-[#002FA7]" />
              </Link>
            </div>
          </div>
        </div>

        {/* Department Stats for Admins */}
        {['super_admin', 'org_admin'].includes(user?.role) && stats?.department_stats?.length > 0 && (
          <div className="mt-6 card-dashboard p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-900">Abteilungsleistung</h2>
              <Link to="/departments" className="text-sm text-[#002FA7] hover:underline flex items-center gap-1">
                Alle ansehen <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="table-header text-left">Abteilung</th>
                    <th className="table-header text-right">Kampagnen</th>
                    <th className="table-header text-right">Einreichungen</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.department_stats.map((dept) => (
                    <tr key={dept.department_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-medium text-zinc-900">{dept.department_name}</td>
                      <td className="py-3 px-4 text-right text-zinc-600">{dept.campaign_count}</td>
                      <td className="py-3 px-4 text-right text-zinc-600">{dept.submission_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
